/**
 * The one API client. sessionStorage token + Authorization: Bearer — no cookies
 * (Chrome blocks cross-site cookies; v1 shipped a cookie path that never worked).
 */
import { idb } from './idb';
import { isGlobusDegraded } from './globusHealth';

const TOKEN_KEY = 'kc_access_token';
// Owned by lib/brand.ts; duplicated here only to purge it on logout without a
// circular import (brand → portal → api).
const LAST_BRAND_KEY = 'aab_last_brand';

/** A server error that retains a machine-readable code when the API provides one. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    /** The HTTP status of the response that produced this error. Undefined
     *  only for a true network/timeout failure (fetch never got a response) —
     *  callers use that distinction to tell a genuine connectivity problem
     *  apart from a structured server rejection (mirrors RefereeResponsePage's
     *  PublicApiError, which already carried this). */
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Decode a JWT's payload without verifying the signature — client-side UX
 *  only (expiry check, `sub` for local tagging), never a trust boundary. The
 *  server independently and authoritatively validates the token on every
 *  request regardless of what this returns. Returns null for a missing or
 *  malformed token rather than throwing, so callers can fail safe.
 *
 *  A JWT segment is base64url (RFC 7515: `-`/`_` instead of `+`/`/`, no
 *  padding) carrying UTF-8, so it is translated and padded before `atob`, then
 *  decoded as UTF-8. Plain `atob` throws on `-`/`_`, which any non-ASCII claim
 *  (e.g. a name with a curly apostrophe) can introduce, and that locked such
 *  applicants out of sign-in. */
function decodeTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const [, segment] = token.split('.');
    if (!segment) return null;
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export const auth = {
  getToken: () => sessionStorage.getItem(TOKEN_KEY),
  setToken: (t: string) => sessionStorage.setItem(TOKEN_KEY, t),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
  /** The signed-in user's Keycloak `sub`, decoded from the access token's
   *  payload (unverified — used only to tag/guard device-local data, never for
   *  a trust decision). Null when signed out or the token is malformed. */
  getSub: (): string | null => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (!t) return null;
    const sub = decodeTokenPayload(t)?.sub;
    return typeof sub === 'string' ? sub : null;
  },
  /**
   * True once the token's `exp` claim (seconds since epoch) is in the past, or
   * the stored token can't be decoded / has no `exp` claim at all. This is a
   * PROACTIVE, client-side-only check — it lets route guards redirect BEFORE
   * firing a request that would otherwise only discover the expiry reactively
   * on a 401 round-trip (the pre-existing behaviour in `api()` below, which
   * remains the real enforcement point regardless of what this returns — this
   * is UX, not a security boundary).
   * Returns false when there is no token at all: "signed out" and "expired"
   * are different states for callers (see `isAuthenticated`, which is what
   * route guards should actually use).
   */
  isExpired: (): boolean => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (!t) return false;
    const exp = decodeTokenPayload(t)?.exp;
    if (typeof exp !== 'number') return true; // malformed/missing claim — fail closed
    return Date.now() >= exp * 1000;
  },
  /** The single check route guards should use: a token exists AND has not
   *  passed its `exp`. */
  isAuthenticated: (): boolean => {
    return !!sessionStorage.getItem(TOKEN_KEY) && !auth.isExpired();
  },
  /** Server-side logout (kills Keycloak sessions) then local clear. Best-effort:
   * the local token always clears even if the server call fails. Also wipes all
   * device-local participant data (IndexedDB drafts + queued upload blobs, the
   * remembered brand) so the next user on a shared device inherits nothing. */
  signOut: async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // Already logged out server-side, or offline — local clear still proceeds.
    }
    sessionStorage.removeItem(TOKEN_KEY);
    try {
      await idb.clearAll();
    } catch {
      // IndexedDB unavailable — nothing persisted to clear.
    }
    try {
      localStorage.removeItem(LAST_BRAND_KEY);
    } catch {
      /* storage disabled */
    }
  },
};

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = auth.getToken();
  // Same-origin by default; the deployed client bakes the server URL in at
  // build time (separate Cloud Run services until the domain fronts both).
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const method = (init.method ?? 'GET').toUpperCase();
  // Degraded mode (integration contract — NOT OPTIONAL): once the health
  // poll has seen the Hub's `globus` indicator down, block mutations here
  // instead of sending a request Globus will reject anyway. /auth/* is
  // exempt — it's Keycloak-backed, not Globus, and sign-out must keep
  // working during a Globus outage. Reads are exempt too: the contract calls
  // for showing last-known data during an outage, not blanking the page.
  if (method !== 'GET' && !path.startsWith('/auth') && isGlobusDegraded()) {
    throw new Error(
      "This action is unavailable right now because we can't reach Globus. Please try again shortly.",
    );
  }
  // Reads get a hard timeout: a hung backend previously left the page on its
  // skeleton forever (fetch has no default timeout). Writes are exempt — a
  // slow-network document submit must not be aborted mid-flight — and a
  // caller-provided signal always wins.
  const signal = init.signal ?? (method === 'GET' ? AbortSignal.timeout(20_000) : undefined);
  let res: Response;
  try {
    res = await fetch(`${base}/api/v1${path}`, {
      ...init,
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    if ((e as Error).name === 'TimeoutError') {
      throw new Error('The portal is taking too long to respond — please try again.');
    }
    throw e;
  }
  if (res.status === 401 && path !== '/auth/login') {
    // Only PORTAL auth failures are 401 (the BFF maps upstream Globus denials
    // to 403) — safe to treat as an expired session. A 401 from the login
    // call itself is just wrong credentials; redirecting would reload
    // /sign-in and wipe the error before the user sees it.
    auth.clear();
    window.location.assign('/sign-in');
    throw new Error('unauthenticated');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    const code =
      typeof body?.error?.code === 'string'
        ? body.error.code
        : typeof body?.code === 'string'
          ? body.code
          : // Some Hub rejections (e.g. reference-management: { error: 'DUPLICATE_REFEREE',
            // submissionId }) put the bare code directly in `error` with no separate
            // human message at all. A SCREAMING_SNAKE_CASE string is never legitimate
            // prose (contrast body.error as a real sentence, e.g. eligibility's
            // { code: 'ERROR', error: 'countryCode... are all required' }, which is
            // already caught by the body?.code branch above), so it's safe to treat as
            // a code without risking misclassifying an actual message.
            typeof body?.error === 'string' && /^[A-Z][A-Z0-9_]*$/.test(body.error)
            ? body.error
            : undefined;
    // Some Hub validation rejections (e.g. the Skills & Hobbies word-count
    // minimum) put the real, field-specific detail in a plural `errors` map
    // keyed by field path, alongside a generic top-level `message` that reads
    // like a network failure. Prefer the field detail when present — Asana
    // 1217449906140125 was a real rejection ("write at least 20 words...")
    // showing as "Something went wrong on our end" because this only ever
    // checked singular `body.error`/`body.message`.
    const fieldErrors =
      body?.errors && typeof body.errors === 'object' && !Array.isArray(body.errors)
        ? Object.values(body.errors as Record<string, unknown>).filter(
            (v): v is string => typeof v === 'string',
          )
        : undefined;
    const message =
      body?.error?.message ??
      (fieldErrors && fieldErrors.length > 0 ? fieldErrors.join(' ') : undefined) ??
      body?.message ??
      body?.error ??
      `${init.method ?? 'GET'} ${path} → ${res.status}`;
    throw new ApiError(typeof message === 'string' ? message : JSON.stringify(message), code, res.status);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}
